
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmailProtocolSelector } from "./EmailProtocolSelector";
import { Loader2 } from "lucide-react";

interface ImapFormProps {
  accountName: string;
  setAccountName: (name: string) => void;
  onSuccess: () => void;
}

// Define the preset configuration types properly
interface ImapConfig {
  imapHost: string;
  imapPort: string;
  smtpHost: string;
  smtpPort: string;
  useTLS: boolean;
}

interface ExchangeConfig extends ImapConfig {
  exchangeUrl: string;
}

type ProviderConfig = ImapConfig | ExchangeConfig;

export const ImapForm = ({ accountName, setAccountName, onSuccess }: ImapFormProps) => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [protocol, setProtocol] = useState("imap");
  
  // IMAP settings
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("");
  const [useTLS, setUseTLS] = useState(true);
  
  // Exchange settings
  const [exchangeUrl, setExchangeUrl] = useState("");
  const [domain, setDomain] = useState("");
  const [authType, setAuthType] = useState("basic");
  
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [provider, setProvider] = useState("custom");

  // Preset configurations for common email providers
  const presetConfigurations: Record<string, ProviderConfig> = {
    outlook: {
      imapHost: "outlook.office365.com",
      imapPort: "993",
      smtpHost: "smtp.office365.com",
      smtpPort: "587",
      useTLS: true,
      exchangeUrl: "https://outlook.office365.com/EWS/Exchange.asmx"
    },
    yahoo: {
      imapHost: "imap.mail.yahoo.com",
      imapPort: "993",
      smtpHost: "smtp.mail.yahoo.com",
      smtpPort: "465",
      useTLS: true
    },
    zoho: {
      imapHost: "imap.zoho.com",
      imapPort: "993",
      smtpHost: "smtp.zoho.com",
      smtpPort: "587",
      useTLS: true
    },
    aol: {
      imapHost: "imap.aol.com",
      imapPort: "993",
      smtpHost: "smtp.aol.com",
      smtpPort: "587",
      useTLS: true
    },
    gmx: {
      imapHost: "imap.gmx.com",
      imapPort: "993",
      smtpHost: "mail.gmx.com",
      smtpPort: "587",
      useTLS: true
    },
    custom: {
      imapHost: "",
      imapPort: "",
      smtpHost: "",
      smtpPort: "",
      useTLS: true
    }
  };

  const handleProviderChange = (value: string) => {
    setProvider(value);
    
    if (value !== "custom") {
      const config = presetConfigurations[value as keyof typeof presetConfigurations];
      setImapHost(config.imapHost);
      setImapPort(config.imapPort);
      setSmtpHost(config.smtpHost);
      setSmtpPort(config.smtpPort);
      setUseTLS(config.useTLS);
      
      // Type guard to check if exchangeUrl exists in the config
      if ('exchangeUrl' in config) {
        setExchangeUrl(config.exchangeUrl);
      }
    }
  };

  const testConnection = async () => {
    setIsTestingConnection(true);
    try {
      const requestBody: any = {
        action: 'test',
        user: email,
        password,
        protocol
      };
      
      if (protocol === 'imap') {
        requestBody.imapHost = imapHost;
        requestBody.imapPort = parseInt(imapPort);
        requestBody.smtpHost = smtpHost;
        requestBody.smtpPort = parseInt(smtpPort);
        requestBody.tls = useTLS;
      } else if (protocol === 'exchange') {
        requestBody.exchangeUrl = exchangeUrl;
        requestBody.domain = domain || undefined;
        requestBody.authType = authType;
      }

      const { data, error } = await supabase.functions.invoke('email-operations', {
        body: requestBody
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.message || 'Connection failed');

      toast({
        title: "Connection Successful",
        description: "Email account connection verified successfully.",
      });
      return true;
    } catch (error: any) {
      console.error('Connection test failed:', error);
      toast({
        title: "Connection Failed",
        description: error.message || "Could not connect to email server. Please check your settings.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields based on protocol
    if (protocol === 'imap' && (!imapHost || !imapPort || !smtpHost || !smtpPort)) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields for IMAP/SMTP connection.",
        variant: "destructive",
      });
      return;
    }
    
    if (protocol === 'exchange' && !exchangeUrl) {
      toast({
        title: "Missing Information",
        description: "Please enter the Exchange server URL.",
        variant: "destructive",
      });
      return;
    }
    
    const connectionSuccessful = await testConnection();
    if (!connectionSuccessful) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      let credentials: any = {};
      
      if (protocol === 'imap') {
        credentials = {
          imap: { host: imapHost, port: parseInt(imapPort), tls: useTLS },
          smtp: { host: smtpHost, port: parseInt(smtpPort), tls: useTLS },
          password
        };
      } else if (protocol === 'exchange') {
        credentials = {
          exchange: { 
            url: exchangeUrl,
            domain: domain || undefined,
            authType
          },
          password
        };
      }

      const { error } = await supabase.from('email_accounts').insert({
        name: accountName || email,
        email,
        provider: protocol,
        credentials,
        user_id: session.user.id,
        include_signature: true
      });

      if (error) throw error;

      onSuccess();
      toast({
        title: "Account Added",
        description: `${protocol === 'exchange' ? 'Exchange' : 'IMAP'} account has been connected successfully`,
      });
    } catch (error: any) {
      console.error('Error saving email account:', error);
      toast({
        title: "Error",
        description: error.message || `Failed to save ${protocol === 'exchange' ? 'Exchange' : 'IMAP'} account`,
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="accountName">Account Name (Optional)</Label>
        <Input
          id="accountName"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          placeholder="My Work Email"
        />
      </div>
      
      <div>
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      
      <div>
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      
      <EmailProtocolSelector 
        value={protocol} 
        onChange={setProtocol} 
      />
      
      <div>
        <Label htmlFor="provider">Email Provider</Label>
        <Select value={provider} onValueChange={handleProviderChange}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a provider or enter custom settings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="outlook">Outlook/Office 365</SelectItem>
            <SelectItem value="yahoo">Yahoo Mail</SelectItem>
            <SelectItem value="zoho">Zoho Mail</SelectItem>
            <SelectItem value="aol">AOL Mail</SelectItem>
            <SelectItem value="gmx">GMX Mail</SelectItem>
            <SelectItem value="custom">Custom Server</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {protocol === 'imap' ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="imapHost">IMAP Host</Label>
              <Input
                id="imapHost"
                required
                value={imapHost}
                onChange={(e) => setImapHost(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="imapPort">IMAP Port</Label>
              <Input
                id="imapPort"
                required
                value={imapPort}
                onChange={(e) => setImapPort(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="smtpHost">SMTP Host</Label>
              <Input
                id="smtpHost"
                required
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="smtpPort">SMTP Port</Label>
              <Input
                id="smtpPort"
                required
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="tls"
              checked={useTLS}
              onCheckedChange={setUseTLS}
            />
            <Label htmlFor="tls">Use TLS</Label>
          </div>
        </>
      ) : (
        <>
          <div>
            <Label htmlFor="exchangeUrl">Exchange Server URL</Label>
            <Input
              id="exchangeUrl"
              required
              value={exchangeUrl}
              onChange={(e) => setExchangeUrl(e.target.value)}
              placeholder="https://outlook.office365.com/EWS/Exchange.asmx"
            />
          </div>
          <div>
            <Label htmlFor="domain">Domain (Optional)</Label>
            <Input
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="company.com"
            />
          </div>
          <div>
            <Label htmlFor="authType">Authentication Type</Label>
            <Select value={authType} onValueChange={setAuthType}>
              <SelectTrigger>
                <SelectValue placeholder="Select authentication type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic Authentication</SelectItem>
                <SelectItem value="ntlm">NTLM Authentication</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      
      <Button type="submit" className="w-full" disabled={isTestingConnection}>
        {isTestingConnection ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Testing Connection...
          </>
        ) : (
          `Connect ${protocol === 'exchange' ? 'Exchange' : 'IMAP'} Account`
        )}
      </Button>
    </form>
  );
};
